import { Fragment } from "react";
import { useTranslation } from "../context/TranslationContext";
import Avatar from "./ds/Avatar";

/*
  Figma "Space hero" (DS · კომპონენტები), for another person's profile: the banner, then
  avatar, name, stats, info and links, with the actions at the end of the row. On a phone
  the actions drop below the text. `children` are the actions.
*/

const SOCIALS = [
  ["instagram", "Instagram"],
  ["tiktok", "TikTok"],
  ["youtube", "YouTube"],
  ["facebook", "Facebook"],
];

const TAG =
  "inline-flex h-6 items-center rounded-radius-sm bg-fill-secondary px-space-8 text-caps-button-s uppercase text-label-primary transition-colors hover:text-accent-text";

const ProfileHero = ({ person, avatar, banner, socialUrl, followStats, onOpenFollowList, children }) => {
  const { t, language } = useTranslation();
  const profile = person?.profile || {};
  const name = person?.username || person?.businessName || t("common.user");
  const joined =
    person?.createdAt &&
    new Date(person.createdAt).toLocaleDateString(language === "ka" ? "ka-GE" : "en-US", { month: "short", year: "numeric" });
  const info = [profile.location, profile.bio || t("profile.noBioYet")].filter(Boolean).join(" · ");
  const socials = SOCIALS.filter(([key]) => profile.socials?.[key]);
  const followLabel = (key) => {
    if (key === "following") return t("follows.followingCount");
    return t(followStats.followers === 1 ? "follows.followerCountOne" : "follows.followerCount");
  };

  return (
    <section className="overflow-hidden rounded-radius-lg bg-bg-raised">
      <div className="h-[240px] w-full bg-fill-quaternary">
        {banner && <img src={banner} alt="" className="h-full w-full object-cover" />}
      </div>
      <div className="flex flex-wrap gap-space-16 px-space-24 py-space-16">
        <Avatar size="xl" src={avatar} name={name} />
        <div className="flex min-w-0 flex-1 basis-[200px] flex-col gap-space-12">
          <div className="flex flex-col gap-space-4">
            <h1 className="break-words text-title-2 text-label-primary">{name}</h1>
            {joined && <p className="text-footnote text-label-secondary">{t("profile.joinedOn", { date: joined })}</p>}
            {followStats && (
              <div className="flex flex-wrap items-center gap-x-space-4 text-footnote-semibold text-label-secondary">
                {["followers", "following"].map((key, index) => (
                  <Fragment key={key}>
                    {index > 0 && <span aria-hidden="true">·</span>}
                    <button type="button" onClick={() => onOpenFollowList(key)} className="lowercase transition hover:underline">
                      {followStats[key]} {followLabel(key)}
                    </button>
                  </Fragment>
                ))}
                {followStats.followsYou && (
                  <span className="ml-space-4 rounded-radius-xs bg-fill-tertiary px-space-8 text-label-secondary">
                    {t("follows.followsYou")}
                  </span>
                )}
              </div>
            )}
            <p className="whitespace-pre-line break-words text-footnote text-label-tertiary">{info}</p>
          </div>
          {(socials.length > 0 || profile.website) && (
            <div className="flex flex-wrap gap-space-8">
              {socials.map(([key, label]) => (
                <a key={key} href={socialUrl(key, profile.socials[key])} target="_blank" rel="noreferrer" className={TAG}>
                  {label}
                </a>
              ))}
              {profile.website && (
                <a href={profile.website} target="_blank" rel="noreferrer" className={TAG}>
                  {t("profile.website")}
                </a>
              )}
            </div>
          )}
        </div>
        {children && <div className="flex flex-wrap items-center gap-space-8 self-start">{children}</div>}
      </div>
    </section>
  );
};

export default ProfileHero;
